// ** React Imports
import { useEffect, useRef, useState } from 'react';

import { client } from 'src/utils/axios-client';
import {
    LeadScreenStore,
    LeadActivity,
    Lead,
    NextActivity,
    LeadAccountStore,
    ViewType,
    ActivityDialogStore,
    LeadCompanyStore,
    CompanyDialogStore,
    LeadSprintStore,
    SprintDialogStore,
    LeadActivityHistoryStore,
    ActivityHistoryDialogStore,
    LeadActivityCadenceStore,
    CadenceListDialogStore,
    LeadActivityOutcomeStore,
    ActivityOutcomes,
    LeadActivitySMSStore,
} from 'src/stores/LeadStore';
import { getActivityEndpoint, leadEndpoints } from '../../../configs/endpoints';

// ** MUI Imports
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import Tooltip from '@mui/material/Tooltip';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import _ from 'lodash';
import Badge, { BadgeProps } from '@mui/material/Badge';
import Button from '@mui/material/Button';
import Icon from 'src/features/common/CustomIcon';
import MenuItem from '@mui/material/MenuItem';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import ListItemIcon from '@mui/material/ListItemIcon';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { ProblemDetails } from '../../../types/ApiErrorResponse';
import SaveTwoToneIcon from '@mui/icons-material/SaveTwoTone';
import FormControl from '@mui/material/FormControl';
import LocalPhoneTwoToneIcon from '@mui/icons-material/LocalPhoneTwoTone';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import DoActivityOutcomes from './DoActivityOutcomes';
import Alert from '@mui/material/Alert';
import SendIcon from '@mui/icons-material/Send';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import StandardSkeleton from '../../common/StandardSkeleton';
import HtmlTextAreaView from '../../common2/Templating/HtmlTextAreaView';
import ContentEditor from '../../../v2/components/ContentEditor/ContentEditor';
import { TokenType } from '../../../v2/components/ContentEditor/types/content-editor-types';

const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
    '& .MuiBadge-badge': {
        right: 33,
        top: 10,
        border: `2px solid ${theme.palette.background.paper}`,
        padding: '0 4px',
    },
}));

type StatusUpdate = { messageId: string; status: string };

const DoActivityOutboundWhatsApp = () => {
    const outcomesRecord = LeadActivityOutcomeStore.useState((s) => s.record);
    const [dataLoaded, setDataLoaded] = useState(false);
    const selectedLead = LeadScreenStore.useState((s) => s.selectedLead);
    const [phoneFrom, setPhoneFrom] = useState(outcomesRecord.phoneFrom);
    const [phoneTo, setPhoneTo] = useState(outcomesRecord.phoneTo);
    const smsMessage = LeadActivitySMSStore.useState((s) => s.record);
    const [smsBodyError, setSmsBodyError] = useState(false);
    const [sendButtonDisabled, setSendButtonDisabled] = useState(false);
    const [connection, setConnection] = useState<null | HubConnection>(null);
    const latestStatus = useRef({ messageId: '', status: '' } as StatusUpdate);
    const [status, setStatus] = useState<StatusUpdate>({ messageId: '', status: '' });
    const [messageId, setMessageId] = useState('');
    const [sentStatus, setSentStatus] = useState('');
    const [showMessageStatus, setShowMessageStatus] = useState(false);
    latestStatus.current = status;

    useEffect(() => {
        const fetchOutcomes = async () => {
            const response = await client.get(
                leadEndpoints.getactivityoutcomes.replace('{leadid}', '' + selectedLead?.id.toString()),
            );
            LeadActivityOutcomeStore.update((s) => {
                s.record = response.data;
            });
            setPhoneFrom(response.data.phoneFrom);
            setPhoneTo(response.data.phoneTo);

            LeadActivitySMSStore.update((s) => {
                s.record.leadId = response.data.leadId;
            });
            LeadActivitySMSStore.update((s) => {
                s.record.smsBody = response.data.smsBody;
            });
            LeadActivitySMSStore.update((s) => {
                s.record.phoneTo = response.data.phoneTo;
            });
            LeadActivitySMSStore.update((s) => {
                s.record.phoneFrom = response.data.phoneFrom;
            });
            setDataLoaded(true);
        };
        if (selectedLead != null) fetchOutcomes();
    }, []);

    const handlePhoneFromChange = (event: SelectChangeEvent) => {
        setPhoneFrom(event.target.value as string);
        LeadActivityOutcomeStore.update((s) => {
            s.record.phoneFrom = event.target.value;
        });
        LeadActivitySMSStore.update((s) => {
            s.record.phoneFrom = event.target.value;
        });
    };

    const handlePhoneToChange = (event: SelectChangeEvent) => {
        setPhoneTo(event.target.value as string);
        LeadActivityOutcomeStore.update((s) => {
            s.record.phoneTo = event.target.value;
        });
        LeadActivitySMSStore.update((s) => {
            s.record.phoneTo = event.target.value;
        });
    };

    const handleBodyTextChange = (value: string) => {
        LeadActivityOutcomeStore.update((s) => {
            s.record.smsBody = value;
        });
        LeadActivitySMSStore.update((s) => {
            s.record.smsBody = value;
        });
    };

    useEffect(() => {
        const newConnection = new HubConnectionBuilder().withUrl('/hubs/twilio').withAutomaticReconnect().build();

        setConnection(newConnection);
    }, []);

    useEffect(() => {
        if (connection) {
            connection
                .start()
                .then((result) => {
                    console.log('Connected!');

                    connection.on('UpdateStatus', (message) => {
                        const updatedStatus = { ...message };
                        setStatus(updatedStatus);
                    });
                })
                .catch((e) => console.log('Connection failed: ', e));
        }
    }, [connection]);

    useEffect(() => {
        if (messageId != '' && status.messageId.toLowerCase() == messageId.toLowerCase()) {
            setSentStatus('Message ' + status.status);
            setShowMessageStatus(true);
        }
    }, [status]);

    const sendSMS = async () => {
        //Send data backend for SMS via SDK
        let hasError = false;
        if (_.isEmpty(outcomesRecord.smsBody)) {
            setSmsBodyError(true);
            hasError = true;
        }

        if (hasError) return;

        await client
            .post(leadEndpoints.sendsmsmessage, smsMessage)
            .then(async (response) => {
                setMessageId(response.data.messageId);
                LeadActivityOutcomeStore.update((s) => {
                    s.record.actionResult = response.data.status;
                });
                LeadActivityOutcomeStore.update((s) => {
                    s.record.actionId = response.data.messageId;
                });
                if (response.data.status == 'SUBMITTED') {
                    LeadActivityOutcomeStore.update((s) => {
                        s.record.saveDisabled = false;
                    });
                    setSendButtonDisabled(true);
                } else {
                    //Show error message
                    setSentStatus(response.data.errorMessage);
                    setShowMessageStatus(true);
                    LeadActivityOutcomeStore.update((s) => {
                        s.record.saveDisabled = false;
                    });
                    setSendButtonDisabled(true);
                }
            })
            .catch((err: ProblemDetails) => {
                if (err.errors) {
                    for (const [key, value] of Object.entries(err.errors)) {
                        const errorMessage = '';

                        value.forEach((message) => {
                            message += message + '\\';
                        });
                    }
                }
            });
    };

    if (dataLoaded) {
        return (
            <Box>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Card variant="outlined">
                            <CardContent>
                                <Box>
                                    <Grid container alignContent="left" spacing={2}>
                                        <Grid item xs={12} sx={{ alignItems: 'right' }} justifyContent={'flex-end'}>
                                            <Typography sx={{ fontSize: 20, color: 'CornflowerBlue' }} gutterBottom>
                                                Outgoing WhatsApp Message
                                            </Typography>
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            md={5}
                                            alignContent="left"
                                            justifyContent={'left'}
                                            sx={{ pt: 1, display: 'flex', alignItems: 'left', flexDirection: 'column' }}
                                        >
                                            <FormControl fullWidth>
                                                <InputLabel htmlFor="phoneFrom" id="-label2">
                                                    Your phone number
                                                </InputLabel>
                                                <Select
                                                    id="phoneFrom"
                                                    label="Your phone number"
                                                    value={phoneFrom}
                                                    size="small"
                                                    fullWidth
                                                    displayEmpty
                                                    onChange={handlePhoneFromChange}
                                                >
                                                    {outcomesRecord.userPhoneNumbers.map((record) => (
                                                        <MenuItem key={record.sid} value={record.number}>
                                                            <Grid container spacing={2} justifyContent={'left'}>
                                                                <Grid item xs={12} md={2}>
                                                                    <Tooltip title="">
                                                                        <ListItemIcon>
                                                                            <SaveTwoToneIcon sx={{ opacity: 0 }} />
                                                                        </ListItemIcon>
                                                                    </Tooltip>
                                                                </Grid>
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                    md={10}
                                                                    justifyContent={'left'}
                                                                    alignItems="left"
                                                                >
                                                                    <Tooltip
                                                                        title={
                                                                            record.countryName + ' ' + record.location
                                                                        }
                                                                        key={record.outcomeId}
                                                                    >
                                                                        <Typography
                                                                            variant="inherit"
                                                                            noWrap
                                                                            align="left"
                                                                        >
                                                                            {record.number}
                                                                        </Typography>
                                                                    </Tooltip>
                                                                </Grid>
                                                            </Grid>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            md={5}
                                            alignContent="left"
                                            justifyContent={'left'}
                                            sx={{ pt: 1, display: 'flex', alignItems: 'left', flexDirection: 'column' }}
                                        >
                                            <FormControl fullWidth>
                                                <InputLabel htmlFor="phoneTo" id="-label">
                                                    Number to text
                                                </InputLabel>
                                                <Select
                                                    id="phoneTo"
                                                    label="Number to text"
                                                    value={phoneTo}
                                                    size="small"
                                                    fullWidth
                                                    displayEmpty
                                                    onChange={handlePhoneToChange}
                                                >
                                                    {outcomesRecord.leadPhoneNumbers.map((record) => (
                                                        <MenuItem key={record.number} value={record.number}>
                                                            <Grid container spacing={2} justifyContent={'left'}>
                                                                <Grid item xs={12} md={2}>
                                                                    {record.numberIcon && (
                                                                        <Tooltip
                                                                            title={record.numberType}
                                                                            key={record.number}
                                                                        >
                                                                            <ListItemIcon>
                                                                                <Icon icon={record.numberIcon}></Icon>
                                                                            </ListItemIcon>
                                                                        </Tooltip>
                                                                    )}
                                                                </Grid>
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                    md={10}
                                                                    justifyContent={'left'}
                                                                    alignItems="left"
                                                                >
                                                                    <Tooltip
                                                                        title={record.numberType}
                                                                        key={record.number}
                                                                    >
                                                                        <Typography
                                                                            variant="inherit"
                                                                            noWrap
                                                                            align="left"
                                                                        >
                                                                            &nbsp; {record.number}
                                                                        </Typography>
                                                                    </Tooltip>
                                                                </Grid>
                                                            </Grid>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            md={2}
                                            alignItems="center"
                                            sx={{ pt: 1, display: 'flex', flexDirection: 'row' }}
                                        >
                                            {dataLoaded && (
                                                <Tooltip title="Send Message">
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="info"
                                                        startIcon={<SendIcon />}
                                                        sx={{ mr: 3 }}
                                                        onClick={sendSMS}
                                                        disabled={sendButtonDisabled}
                                                    >
                                                        Send
                                                    </Button>
                                                </Tooltip>
                                            )}
                                        </Grid>
                                        {showMessageStatus && (
                                            <Grid
                                                item
                                                xs={12}
                                                md={12}
                                                alignContent="left"
                                                justifyContent={'left'}
                                                sx={{
                                                    pt: 1,
                                                    display: 'flex',
                                                    alignItems: 'left',
                                                    flexDirection: 'column',
                                                }}
                                            >
                                                <Alert severity="info">{sentStatus}</Alert>
                                            </Grid>
                                        )}
                                        <Grid
                                            item
                                            xs={12}
                                            md={12}
                                            alignContent="left"
                                            justifyContent={'left'}
                                            sx={{ pt: 1, display: 'flex', alignItems: 'left', flexDirection: 'column' }}
                                        >
                                            <FormControl fullWidth>
                                                <ContentEditor
                                                    sx={{ height: 150 }}
                                                    html={outcomesRecord.smsBody}
                                                    onChange={handleBodyTextChange}
                                                    error={smsBodyError}
                                                    disabled={false}
                                                    tokenType={TokenType.None}
                                                    showToolbar={false}
                                                />
                                            </FormControl>
                                            {smsBodyError && (
                                                <Typography variant="caption" noWrap sx={{ color: 'red' }} align="left">
                                                    SMS message is required!
                                                </Typography>
                                            )}
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            md={12}
                                            alignContent="left"
                                            justifyContent={'left'}
                                            sx={{ pt: 1, display: 'flex', alignItems: 'left', flexDirection: 'column' }}
                                        >
                                            <DoActivityOutcomes />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        );
    } else {
        return <StandardSkeleton />;
    }
};

export { DoActivityOutboundWhatsApp };

export default DoActivityOutboundWhatsApp;
